import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Badge, Button, TextField } from "../ui";
import {
  FeatherCheck,
  FeatherChevronDown,
  FeatherSearch,
  FeatherLoader,
  FeatherX,
} from "@subframe/core";
import { VendorBill, VendorListItem } from "../types";
import {
  useToast,
  SearchableDropdown,
} from "../shared/presentation/components";
import { useDropdownCache } from "../shared/presentation/hooks";
import { getAllVendors, getVendorById } from "../services/vendorApi";
import { GetBillsForVendorUseCase } from "../shared/domain/use-cases/GetBillsForVendorUseCase";
import { BillRepositoryImpl } from "../shared/data/repositories/BillRepositoryImpl";
import { BillListItemEntity } from "../shared/domain/entities/BillListItemEntity";
import { isDocumentFxMismatch } from "../shared/utils/manualDocumentCurrency";

// Permissions
import { usePermissions } from "../shared/presentation/hooks/usePermissions";
import { MODULE_CODES, ACTION_CODES } from "../shared/constants/permissions";

interface SelectedBill {
  bill: VendorBill;
  amount: number;
  adjustmentAmount?: number;
  isSelected: boolean;
}

interface VendorPaymentRecoProps {
  id: string;
  transactionAmount: number;
  currencySymbol: string;
  bankExchangeRate: number | null;
  onBankExchangeRateChange: (rate: number | null) => void;
  showRemoveButton?: boolean;
  onRemove?: () => void;
  isMultipleInstances?: boolean;
  totalAllocatedAmountsFromOtherInstances?: number;
  excludedVendorIds?: string[];
  knockOffOpeningBalance?: boolean;
  onChange: (data: {
    vendorId: string | null;
    vendorName: string | null;
    selectedBills: SelectedBill[];
    advancePayment: number;
    onAccountAmount: number;
    totalBillAmounts: number;
    bankExchangeRate: number | null;
  }) => void;
  autoSelectVendorId?: string | null;
  autoSelectVendorName?: string | null;
  recommendationSource?: string | null;
  recommendationConfidence?: string | number | null;
  onInitialPartyLoaded?: () => void;
}

const VendorPaymentReco = React.forwardRef<
  HTMLDivElement,
  VendorPaymentRecoProps
>(
  (
    {
      id,
      transactionAmount,
      currencySymbol,
      bankExchangeRate,
      onBankExchangeRateChange,
      showRemoveButton = false,
      onRemove,
      isMultipleInstances = false,
      totalAllocatedAmountsFromOtherInstances = 0,
      excludedVendorIds = [],
      knockOffOpeningBalance = false,
      onChange,
      autoSelectVendorId,
      autoSelectVendorName,
      recommendationSource,
      recommendationConfidence,
      onInitialPartyLoaded,
    },
    ref,
  ) => {
    const { showWarning } = useToast();
    const { getCached, setCached } = useDropdownCache();

    // Permissions
    const { getPermissionInfo } = usePermissions();
    const advanceInfo = getPermissionInfo(
      MODULE_CODES.BANK_TRANSACTION,
      ACTION_CODES.ADVANCE,
    );
    const canAdvance = advanceInfo.granted;

    // Vendor selection state
    const [selectedVendor, setSelectedVendor] = useState<VendorListItem | null>(
      null,
    );
    const [loadingZohoParty, setLoadingZohoParty] = useState(false);
    const [vendorFromSuggestion, setVendorFromSuggestion] = useState(false);
    const [vendorsCache, setVendorsCache] = useState<
      Map<string, VendorListItem>
    >(new Map());
    const autoSelectAttemptedRef = useRef(false);

    // Bill fetching state (vendorBills not needed as we use selectedBills)
    const [selectedBills, setSelectedBills] = useState<SelectedBill[]>([]);
    const [loadingBills, setLoadingBills] = useState(false);
    const [billsOffset, setBillsOffset] = useState(0);
    const [billsHasMore, setBillsHasMore] = useState(false);
    const [billsLoading, setBillsLoading] = useState(false);
    const [billSearchTerm, setBillSearchTerm] = useState<string>("");

    // Advance payment state
    const [advancePayment, setAdvancePayment] = useState<number>(0);
    const [payInFullUsed, setPayInFullUsed] = useState(false);

    // Local input state
    const [bankExchangeRateInput, setBankExchangeRateInput] =
      useState<string>("");
    const [billAmountInputs, setBillAmountInputs] = useState<
      Record<string, string>
    >({});
    const [billAdjustmentInputs, setBillAdjustmentInputs] = useState<
      Record<string, string>
    >({});
    const [advancePaymentInput, setAdvancePaymentInput] = useState<string>("");
    const [isBankExchangeRateFocused, setIsBankExchangeRateFocused] =
      useState(false);
    const [isAdvancePaymentFocused, setIsAdvancePaymentFocused] =
      useState(false);
    const focusedBillInputsRef = useRef<Set<string>>(new Set());

    const handleNumberInputWheel = useCallback(
      (event: React.WheelEvent<HTMLInputElement>) => {
        event.preventDefault();
        event.currentTarget.blur();
      },
      [],
    );

    // Search timeout ref for debouncing
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const prevSearchTermRef = useRef<string>("");

    // Initialize use case
    const getBillsUseCase = useMemo(
      () => new GetBillsForVendorUseCase(new BillRepositoryImpl()),
      [],
    );

    // Helper function to convert BillListItemEntity to VendorBill
    const convertToVendorBill = (entity: BillListItemEntity): VendorBill => {
      return {
        bill_id: entity.bill_id,
        bill_number: entity.bill_number,
        vendor_id: entity.vendor_id,
        vendor_name: entity.vendor_name,
        total: entity.total,
        balance: entity.balance,
        tds_total: entity.tds_total,
        currency_code: entity.currency_code,
        currency_symbol: entity.currency_symbol,
        exchange_rate: entity.exchange_rate,
        status: entity.status,
      };
    };

    // Fetch vendors async function for SearchableDropdown
    const fetchVendors = useCallback(
      async (
        searchTerm: string,
        limit: number,
        offset: number,
        selectedVendorId?: string,
        selectedVendorName?: string,
      ): Promise<{
        items: VendorListItem[];
        hasMore?: boolean;
      }> => {
        const shouldCache = offset === 0 && !searchTerm;
        const cacheKey = { searchTerm, limit, offset };

        if (shouldCache) {
          const cached = getCached<{
            items: VendorListItem[];
            hasMore?: boolean;
          }>("vendors", cacheKey);

          if (cached) {
            if (offset === 0 && selectedVendorId && selectedVendorName) {
              const hasSelected = cached.items.some(
                (vendor: VendorListItem) =>
                  vendor.zoho_vendorid === selectedVendorId,
              );

              if (!hasSelected) {
                const cachedVendor = vendorsCache.get(selectedVendorId);
                if (cachedVendor) {
                  return {
                    items: [cachedVendor, ...cached.items],
                    hasMore: cached.hasMore,
                  };
                }

                try {
                  const selectedResponse =
                    await getVendorById(selectedVendorId);
                  if (selectedResponse.data) {
                    const mappedVendor: VendorListItem = {
                      vendorid: selectedResponse.data.vendorid.toString(),
                      zoho_vendorid: selectedResponse.data.zoho_vendorid,
                      vendor_legal_name:
                        selectedResponse.data.vendor_legal_name,
                      currency_id: selectedResponse.data.currency || "",
                    };
                    setVendorsCache((prev) => {
                      const newCache = new Map(prev);
                      newCache.set(selectedVendorId, mappedVendor);
                      return newCache;
                    });
                    return {
                      items: [mappedVendor, ...cached.items],
                      hasMore: cached.hasMore,
                    };
                  }
                } catch (error) {
                  console.warn(
                    "Could not fetch selected vendor from backend:",
                    error,
                  );
                }
              }
            }
            return cached;
          }
        }

        try {
          const response = await getAllVendors({
            search: searchTerm || undefined,
            limit,
            offset,
          });

          let items = response.data || [];

          // Filter out excluded vendor IDs (already selected in other instances)
          if (excludedVendorIds && excludedVendorIds.length > 0) {
            items = items.filter(
              (vendor) => !excludedVendorIds.includes(vendor.zoho_vendorid),
            );
          }

          if (offset === 0 && selectedVendorId && selectedVendorName) {
            const hasSelected = items.some(
              (vendor) => vendor.zoho_vendorid === selectedVendorId,
            );

            if (!hasSelected) {
              const cachedVendor = vendorsCache.get(selectedVendorId);
              if (cachedVendor) {
                items = [cachedVendor, ...items];
              } else {
                try {
                  const selectedResponse =
                    await getVendorById(selectedVendorId);
                  if (selectedResponse.data) {
                    const mappedVendor: VendorListItem = {
                      vendorid: selectedResponse.data.vendorid.toString(),
                      zoho_vendorid: selectedResponse.data.zoho_vendorid,
                      vendor_legal_name:
                        selectedResponse.data.vendor_legal_name,
                      currency_id: selectedResponse.data.currency || "",
                    };
                    setVendorsCache((prev) => {
                      const newCache = new Map(prev);
                      newCache.set(selectedVendorId, mappedVendor);
                      return newCache;
                    });
                    items = [mappedVendor, ...items];
                  }
                } catch (error) {
                  console.warn(
                    "Could not fetch selected vendor from backend:",
                    error,
                  );
                }
              }
            } else {
              const selectedIndex = items.findIndex(
                (vendor) => vendor.zoho_vendorid === selectedVendorId,
              );
              if (selectedIndex >= 0 && selectedIndex > 0) {
                const [selectedVendor] = items.splice(selectedIndex, 1);
                items = [selectedVendor, ...items];
              }
            }
          }

          const result = {
            items,
            hasMore: response.pagination?.has_more || false,
          };

          if (shouldCache) {
            setCached("vendors", cacheKey, {
              items: response.data || [],
              hasMore: response.pagination?.has_more || false,
            });
          }

          return result;
        } catch (error) {
          console.error("Error fetching vendors:", error);
          return {
            items: [],
            hasMore: false,
          };
        }
      },
      [getCached, setCached, vendorsCache, excludedVendorIds],
    );

    // Fetch vendor bills
    const fetchVendorBills = useCallback(
      async (vendorZohoId: string, reset: boolean = true) => {
        if (reset) {
          setBillsOffset(0);
        }

        setLoadingBills(true);
        setBillsLoading(true);
        try {
          const result = await getBillsUseCase.execute(
            vendorZohoId,
            5,
            reset ? 0 : billsOffset,
            billSearchTerm || undefined,
          );

          const billsWithBalance = result.items
            .filter((bill) => bill.balance > 0)
            .map(convertToVendorBill);

          setBillsHasMore(result.pagination.has_more);
          setBillsOffset(result.pagination.offset + result.pagination.limit);

          const newSelectedBills: SelectedBill[] = billsWithBalance.map(
            (bill) => ({
              bill,
              amount: 0,
              isSelected: false,
            }),
          );

          if (reset) {
            setSelectedBills(newSelectedBills);
          } else {
            // Deduplicate by bill_id to prevent duplicates across API calls
            setSelectedBills((prev) => {
              const existingBillIds = new Set(
                prev.map((item) => item.bill.bill_id),
              );
              const uniqueNewBills = newSelectedBills.filter(
                (item) => !existingBillIds.has(item.bill.bill_id),
              );
              return [...prev, ...uniqueNewBills];
            });
          }
        } catch (error) {
          console.error("Failed to fetch vendor bills:", error);
          if (reset) {
            setSelectedBills([]);
          }
        } finally {
          setLoadingBills(false);
          setBillsLoading(false);
        }
      },
      [getBillsUseCase, billsOffset, billSearchTerm],
    );

    // Debounced search effect
    useEffect(() => {
      if (!selectedVendor) return;

      const currentSearchTerm = billSearchTerm.trim();
      const previousSearchTerm = prevSearchTermRef.current.trim();

      // Skip if search term hasn't actually changed
      if (currentSearchTerm === previousSearchTerm) {
        return;
      }

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (!currentSearchTerm && previousSearchTerm) {
        prevSearchTermRef.current = currentSearchTerm;
        setBillsOffset(0);
        fetchVendorBills(selectedVendor.zoho_vendorid, true);
        return;
      }

      if (currentSearchTerm) {
        prevSearchTermRef.current = currentSearchTerm;
        searchTimeoutRef.current = setTimeout(() => {
          if (selectedVendor.zoho_vendorid) {
            setBillsOffset(0);
            fetchVendorBills(selectedVendor.zoho_vendorid, true);
          }
        }, 300);
      } else {
        prevSearchTermRef.current = currentSearchTerm;
      }

      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billSearchTerm, selectedVendor?.zoho_vendorid]);

    // Auto-select vendor if provided
    useEffect(() => {
      const autoSelectVendor = async () => {
        if (
          autoSelectVendorId &&
          autoSelectVendorId !== "create_new" &&
          !selectedVendor &&
          !autoSelectAttemptedRef.current
        ) {
          autoSelectAttemptedRef.current = true;
          setLoadingZohoParty(true);
          try {
            const response = await getVendorById(autoSelectVendorId);
            if (response.success && response.data) {
              const mappedVendor: VendorListItem = {
                vendorid: response.data.vendorid.toString(),
                zoho_vendorid: response.data.zoho_vendorid,
                vendor_legal_name: response.data.vendor_legal_name,
                currency_id: response.data.currency || "",
              };
              setSelectedVendor(mappedVendor);
              setVendorFromSuggestion(true);
              setBillSearchTerm("");
              prevSearchTermRef.current = "";
              onBankExchangeRateChange(null);
              setVendorsCache((prev) => {
                const newCache = new Map(prev);
                newCache.set(mappedVendor.zoho_vendorid, mappedVendor);
                return newCache;
              });
              fetchVendorBills(mappedVendor.zoho_vendorid);
            }
          } catch (error) {
            console.error("Failed to auto-select vendor:", error);
          } finally {
            setLoadingZohoParty(false);
            onInitialPartyLoaded?.();
          }
        }
      };
      autoSelectVendor();
    }, [
      autoSelectVendorId,
      autoSelectVendorName,
      selectedVendor,
      fetchVendorBills,
      onBankExchangeRateChange,
    ]);

    // Check if bill is foreign currency
    const isBillForeignCurrency = useCallback(
      (bill: VendorBill): boolean => isDocumentFxMismatch(bill.currency_code),
      [],
    );

    // Check if any bill has foreign currency
    const hasForeignCurrency = useCallback((): boolean => {
      return selectedBills.some((item) => isBillForeignCurrency(item.bill));
    }, [isBillForeignCurrency, selectedBills]);

    const getBillAdjustmentLimit = useCallback((item: SelectedBill): number => {
      const billBalance = item.bill.balance || 0;
      const remaining = billBalance - Math.max(0, item.amount || 0);
      return Math.max(0, Math.round(remaining * 100) / 100);
    }, []);

    const shouldShowBillAdjustment = useCallback(
      (item: SelectedBill): boolean => {
        if (!item.isSelected) return false;
        if (isBillForeignCurrency(item.bill)) return false;
        const currentAdjustment = item.adjustmentAmount || 0;
        const adjustmentLimit = getBillAdjustmentLimit(item);
        return (
          currentAdjustment > 0 ||
          (adjustmentLimit > 0 && adjustmentLimit <= 10)
        );
      },
      [getBillAdjustmentLimit, isBillForeignCurrency],
    );

    // Get maximum amount that can be entered for a specific bill
    const getMaxAmountForBill = useCallback(
      (billId: string): number => {
        const currentBill = selectedBills.find(
          (item) => item.bill.bill_id === billId,
        );

        if (!currentBill) return 0;

        const isForeign = isBillForeignCurrency(currentBill.bill);
        const billBalance = currentBill.bill.balance || 0;
        const roundedBillBalance = Math.round(billBalance * 100) / 100;

        if (isForeign) {
          return Math.max(0, roundedBillBalance);
        }

        // For multiple instances, consider totalAllocatedAmountsFromOtherInstances
        const totalFromOtherInstances =
          isMultipleInstances && totalAllocatedAmountsFromOtherInstances
            ? totalAllocatedAmountsFromOtherInstances
            : 0;

        const otherBillsInCurrentInstance = selectedBills
          .filter((item) => item.isSelected && item.bill.bill_id !== billId)
          .reduce((sum, item) => {
            return sum + (item.amount || 0);
          }, 0);

        const availableAmount =
          transactionAmount -
          totalFromOtherInstances -
          otherBillsInCurrentInstance;

        const cappedByTransaction = Math.round(availableAmount * 100) / 100;
        return Math.max(0, Math.min(cappedByTransaction, roundedBillBalance));
      },
      [
        isBillForeignCurrency,
        selectedBills,
        isMultipleInstances,
        totalAllocatedAmountsFromOtherInstances,
        transactionAmount,
      ],
    );

    // Calculate advance payment
    const calculateAdvancePayment = useCallback((): number => {
      if (isMultipleInstances) {
        return advancePayment;
      }

      const hasForeign = hasForeignCurrency();
      if (!hasForeign || !bankExchangeRate || bankExchangeRate <= 0) {
        const totalUsedAmount = selectedBills
          .filter((item) => item.isSelected)
          .reduce((sum, item) => {
            return sum + (item.amount || 0);
          }, 0);
        return Math.max(0, transactionAmount - totalUsedAmount);
      }

      const totalUsedAmount = selectedBills
        .filter((item) => item.isSelected)
        .reduce((sum, item) => {
          return sum + (item.amount || 0);
        }, 0);

      if (totalUsedAmount === 0) {
        return transactionAmount / bankExchangeRate;
      }

      const remainingAdvance =
        transactionAmount / bankExchangeRate - totalUsedAmount;
      return Math.max(0, Math.round(remainingAdvance * 100) / 100);
    }, [
      isMultipleInstances,
      advancePayment,
      hasForeignCurrency,
      bankExchangeRate,
      selectedBills,
      transactionAmount,
    ]);

    // Track previous isMultipleInstances to detect transition
    const prevIsMultipleInstancesRef = useRef(isMultipleInstances);

    // Update advance payment when needed
    useEffect(() => {
      // When transitioning from single to multiple instances, clear advance payment
      if (isMultipleInstances && !prevIsMultipleInstancesRef.current) {
        setAdvancePayment(0);
        setAdvancePaymentInput("");
      }

      prevIsMultipleInstancesRef.current = isMultipleInstances;

      if (!isMultipleInstances) {
        const calculated = calculateAdvancePayment();
        setAdvancePayment((prev) =>
          Math.abs(calculated - prev) > 0.01 ? calculated : prev,
        );
      }
    }, [
      isMultipleInstances,
      calculateAdvancePayment,
      selectedBills,
      transactionAmount,
      bankExchangeRate,
    ]);

    useEffect(() => {
      setSelectedBills((prev) => {
        let changed = false;
        const next = prev.map((item) => {
          if (!isBillForeignCurrency(item.bill) || !item.adjustmentAmount) {
            return item;
          }

          changed = true;
          return {
            ...item,
            adjustmentAmount: 0,
          };
        });

        return changed ? next : prev;
      });
    }, [isBillForeignCurrency, selectedBills]);

    // Notify parent of changes
    const onChangeRef = useRef(onChange);
    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      const totalBillAmounts = selectedBills
        .filter((item) => item.isSelected)
        .reduce((sum, item) => sum + (item.amount || 0), 0);

      onChangeRef.current({
        vendorId: selectedVendor?.zoho_vendorid || null,
        vendorName: selectedVendor?.vendor_legal_name || null,
        selectedBills: selectedBills,
        advancePayment: knockOffOpeningBalance ? 0 : advancePayment,
        onAccountAmount: knockOffOpeningBalance ? advancePayment : 0,
        totalBillAmounts: totalBillAmounts,
        bankExchangeRate: bankExchangeRate,
      });
    }, [
      selectedVendor,
      selectedBills,
      advancePayment,
      bankExchangeRate,
      knockOffOpeningBalance,
    ]);

    // Handle vendor selection
    const handleVendorSelect = useCallback(
      (vendor: VendorListItem) => {
        setVendorFromSuggestion(false); // User changed; no longer showing recommendation
        setSelectedBills([]);
        setBillsHasMore(false);
        setBillsOffset(0);
        setLoadingBills(true);
        setBillsLoading(true);
        setBillSearchTerm("");
        prevSearchTermRef.current = "";
        onBankExchangeRateChange(null);

        setSelectedVendor(vendor);
        setVendorsCache((prev) => {
          const newCache = new Map(prev);
          newCache.set(vendor.zoho_vendorid, vendor);
          return newCache;
        });
        fetchVendorBills(vendor.zoho_vendorid);
      },
      [fetchVendorBills, onBankExchangeRateChange],
    );

    // Handle bill selection
    const handleBillSelect = useCallback((billId: string) => {
      setSelectedBills((prev) => {
        const updated = prev.map((item) => {
          if (item.bill.bill_id === billId) {
            const newIsSelected = !item.isSelected;
            return {
              ...item,
              isSelected: newIsSelected,
              amount: newIsSelected ? item.amount : 0,
              adjustmentAmount: newIsSelected ? item.adjustmentAmount : 0,
            };
          }
          return item;
        });

        const hasNewlySelectedWithoutAmount = updated.some(
          (item) => item.isSelected && (item.amount === 0 || !item.amount),
        );

        if (hasNewlySelectedWithoutAmount) {
          setPayInFullUsed(false);
        }

        return updated;
      });
    }, []);

    // Handle amount change
    const handleAmountChange = useCallback(
      (billId: string, amount: number) => {
        setSelectedBills((prev) => {
          const billItem = prev.find((item) => item.bill.bill_id === billId);

          if (!billItem) return prev;

          const roundedAmount = Math.round(amount * 100) / 100;
          const maxAllowed = getMaxAmountForBill(billId);
          const roundedMaxAllowed = Math.round(maxAllowed * 100) / 100;

          const isBillForeign = isBillForeignCurrency(billItem.bill);
          let finalAmount = roundedAmount;

          if (isBillForeign) {
            const billBalance = billItem.bill.balance || 0;
            const roundedBillBalance = Math.round(billBalance * 100) / 100;
            if (finalAmount > roundedBillBalance + 0.001) {
              showWarning(
                `Amount cannot exceed ${
                  billItem.bill.currency_symbol || "₹"
                }${formatAmount(roundedBillBalance)}.`,
              );
              finalAmount = roundedBillBalance;
            }
          } else {
            if (finalAmount > roundedMaxAllowed + 0.001) {
              const currencySymbol =
                billItem.bill.currency_symbol ||
                billItem.bill.currency_code ||
                "INR";
              showWarning(
                `Cannot exceed the max amount of ${currencySymbol} ${formatAmount(
                  roundedMaxAllowed,
                )}.`,
              );
              finalAmount = roundedMaxAllowed;
            }

            const billBalance = billItem.bill.balance || 0;
            const roundedBillBalance = Math.round(billBalance * 100) / 100;
            if (finalAmount > roundedBillBalance + 0.001) {
              showWarning(
                `Amount cannot exceed bill balance of ${
                  billItem.bill.currency_symbol || "₹"
                }${formatAmount(roundedBillBalance)}.`,
              );
              finalAmount = roundedBillBalance;
            }
          }

          const roundedFinalAmount = Math.round(finalAmount * 100) / 100;
          const updated = prev.map((item) => {
            if (item.bill.bill_id !== billId) {
              return item;
            }

            const billBalance = item.bill.balance || 0;
            const remaining =
              Math.round((billBalance - roundedFinalAmount) * 100) / 100;
            const adjustmentAmount =
              !isBillForeignCurrency(item.bill) &&
              remaining > 0 &&
              remaining <= 10
                ? remaining
                : 0;

            return {
              ...item,
              amount: roundedFinalAmount,
              adjustmentAmount,
            };
          });

          setPayInFullUsed(false);

          return updated;
        });
      },
      [getMaxAmountForBill, isBillForeignCurrency, showWarning],
    );

    const handleAdjustmentChange = useCallback(
      (billId: string, amount: number) => {
        const currentItem = selectedBills.find(
          (item) => item.bill.bill_id === billId,
        );
        const isForeignBill = currentItem
          ? isBillForeignCurrency(currentItem.bill)
          : false;
        const adjustmentLimit =
          currentItem && !isForeignBill
            ? getBillAdjustmentLimit(currentItem)
            : 0;
        const roundedAmount = Math.round(Math.max(0, amount) * 100) / 100;
        const finalAmount = isForeignBill
          ? 0
          : Math.min(roundedAmount, adjustmentLimit);
        const previousAdjustment = currentItem?.adjustmentAmount || 0;

        if (
          currentItem &&
          !isForeignBill &&
          roundedAmount > adjustmentLimit + 0.001
        ) {
          showWarning(
            `Adjustment cannot exceed ${
              currentItem.bill.currency_symbol || "₹"
            }${adjustmentLimit.toFixed(2)}.`,
          );
        }

        setSelectedBills((prev) =>
          prev.map((item) => {
            if (item.bill.bill_id !== billId) return item;

            return {
              ...item,
              adjustmentAmount: finalAmount,
            };
          }),
        );

        if (
          isMultipleInstances &&
          (canAdvance || knockOffOpeningBalance) &&
          previousAdjustment > 0 &&
          previousAdjustment <= 10 &&
          finalAmount === 0
        ) {
          setAdvancePayment(
            (prevAdvance) =>
              Math.round((prevAdvance + previousAdjustment) * 100) / 100,
          );
        }
      },
      [
        canAdvance,
        knockOffOpeningBalance,
        getBillAdjustmentLimit,
        isBillForeignCurrency,
        isMultipleInstances,
        selectedBills,
        showWarning,
      ],
    );

    // Handle Pay in Full
    const handlePayInFull = useCallback(() => {
      if (hasForeignCurrency()) {
        showWarning("Pay in Full is unavailable for foreign currency bills.");
        return;
      }
      const transactionAmountINR = transactionAmount;
      const isForeignCurrencyTransaction = hasForeignCurrency();
      const transactionAmountForCalculation =
        isForeignCurrencyTransaction && bankExchangeRate && bankExchangeRate > 0
          ? transactionAmountINR / bankExchangeRate
          : transactionAmountINR;

      if (
        isForeignCurrencyTransaction &&
        (!bankExchangeRate || bankExchangeRate <= 0)
      ) {
        showWarning(
          "Please enter the Bank Exchange Rate before using Pay in Full for foreign currency transactions.",
        );
        return;
      }

      const selectedItemsWithoutAmounts = selectedBills.filter(
        (item) => item.isSelected && (item.amount === 0 || !item.amount),
      );

      if (selectedItemsWithoutAmounts.length === 0) {
        showWarning(
          "Please select at least one bill without an amount to pay in full.",
        );
        return;
      }

      let remainingAmount: number;
      const totalFromOtherInstances =
        isMultipleInstances && totalAllocatedAmountsFromOtherInstances
          ? totalAllocatedAmountsFromOtherInstances
          : 0;

      if (
        isForeignCurrencyTransaction &&
        bankExchangeRate &&
        bankExchangeRate > 0
      ) {
        const currentlyUsedAmountINR = selectedBills
          .filter((item) => item.isSelected && item.amount > 0)
          .reduce((sum, item) => {
            const itemAmount = item.amount || 0;
            return sum + itemAmount * bankExchangeRate;
          }, 0);
        const remainingAmountINR = Math.max(
          0,
          transactionAmountINR - currentlyUsedAmountINR,
        );
        remainingAmount =
          remainingAmountINR > 0
            ? Math.floor((remainingAmountINR / bankExchangeRate) * 100) / 100
            : 0;
      } else {
        const currentlyUsedAmount = selectedBills
          .filter((item) => item.isSelected && item.amount > 0)
          .reduce((sum, item) => {
            const itemAmount = item.amount || 0;
            return sum + itemAmount;
          }, 0);
        remainingAmount = Math.max(
          0,
          transactionAmountForCalculation -
            totalFromOtherInstances -
            currentlyUsedAmount,
        );
      }

      const billsToUpdate: Array<{
        billId: string;
        amount: number;
      }> = [];
      const billPlans = selectedItemsWithoutAmounts.map((item) => {
        const billBalance = Math.round((item.bill.balance || 0) * 100) / 100;
        return {
          billId: item.bill.bill_id,
          billBalance,
          adjustmentCapacity: isBillForeignCurrency(item.bill)
            ? 0
            : Math.min(10, Math.max(0, billBalance)),
        };
      });
      const totalBillBalance = billPlans.reduce(
        (sum, plan) => sum + plan.billBalance,
        0,
      );
      const totalAdjustmentNeeded =
        Math.round(Math.max(0, totalBillBalance - remainingAmount) * 100) / 100;
      const totalAdjustmentCapacity = billPlans.reduce(
        (sum, plan) => sum + plan.adjustmentCapacity,
        0,
      );
      const payInFullAdjustmentsByBillId: Record<string, number> = {};

      if (
        totalAdjustmentNeeded > 0 &&
        totalAdjustmentNeeded <= totalAdjustmentCapacity
      ) {
        let remainingAdjustment = totalAdjustmentNeeded;
        billPlans.forEach((plan, index) => {
          if (remainingAdjustment <= 0) return;
          const remainingPlans = billPlans.length - index;
          const share =
            index === billPlans.length - 1
              ? remainingAdjustment
              : Math.floor((remainingAdjustment / remainingPlans) * 100) / 100;
          const adjustment = Math.min(plan.adjustmentCapacity, share);
          payInFullAdjustmentsByBillId[plan.billId] = adjustment;
          remainingAdjustment =
            Math.round((remainingAdjustment - adjustment) * 100) / 100;
        });
      }
      const cannotPayFull: Array<{
        billNumber: string;
        billBalance: number;
        requiredAmount: number;
      }> = [];

      for (const item of selectedItemsWithoutAmounts) {
        if (remainingAmount <= 0) {
          cannotPayFull.push({
            billNumber: item.bill.bill_number || item.bill.bill_id,
            billBalance: item.bill.balance || 0,
            requiredAmount: item.bill.balance || 0,
          });
          continue;
        }

        const billBalance = item.bill.balance || 0;
        const plannedAdjustment =
          payInFullAdjustmentsByBillId[item.bill.bill_id] || 0;
        let paymentAmount = Math.min(
          Math.max(0, billBalance - plannedAdjustment),
          remainingAmount,
        );

        if (
          isForeignCurrencyTransaction &&
          bankExchangeRate &&
          bankExchangeRate > 0 &&
          remainingAmount > 0
        ) {
          const remainingAmountINR = remainingAmount * bankExchangeRate;
          const maxPaymentAmount =
            Math.floor((remainingAmountINR / bankExchangeRate) * 100) / 100;
          paymentAmount = Math.min(paymentAmount, maxPaymentAmount);
        }

        paymentAmount = Math.round(paymentAmount * 100) / 100;

        if (paymentAmount > billBalance) {
          paymentAmount = billBalance;
          paymentAmount = Math.round(paymentAmount * 100) / 100;
        }

        if (paymentAmount > 0) {
          billsToUpdate.push({
            billId: item.bill.bill_id,
            amount: paymentAmount,
          });

          if (
            isForeignCurrencyTransaction &&
            bankExchangeRate &&
            bankExchangeRate > 0
          ) {
            const currentlyUsedAmountINR = selectedBills
              .filter((bill) => {
                const isAlreadyInUpdate = billsToUpdate.some(
                  (u) => u.billId === bill.bill.bill_id,
                );
                return (
                  (bill.isSelected && bill.amount > 0 && !isAlreadyInUpdate) ||
                  isAlreadyInUpdate
                );
              })
              .reduce((sum, bill) => {
                const updateInfo = billsToUpdate.find(
                  (u) => u.billId === bill.bill.bill_id,
                );
                const billAmount = updateInfo
                  ? updateInfo.amount
                  : bill.amount || 0;
                return sum + billAmount * bankExchangeRate;
              }, 0);
            const remainingAmountINR = Math.max(
              0,
              transactionAmountINR - currentlyUsedAmountINR,
            );
            remainingAmount =
              remainingAmountINR > 0
                ? Math.floor((remainingAmountINR / bankExchangeRate) * 100) /
                  100
                : 0;
          } else {
            remainingAmount -= paymentAmount;
            remainingAmount = Math.round(remainingAmount * 100) / 100;
          }
        } else {
          cannotPayFull.push({
            billNumber: item.bill.bill_number || item.bill.bill_id,
            billBalance: billBalance,
            requiredAmount: billBalance,
          });
        }
      }

      if (cannotPayFull.length > 0) {
        const billNumbers = cannotPayFull
          .map((item) => item.billNumber)
          .join(", ");
        showWarning(
          `The following bills cannot be paid due to insufficient transaction amount: ${billNumbers}. Please deselect them or reduce the amount manually.`,
        );

        if (billsToUpdate.length === 0) {
          return;
        }
      }

      setSelectedBills((prev) => {
        const updated = prev.map((item) => {
          const updateInfo = billsToUpdate.find(
            (u) => u.billId === item.bill.bill_id,
          );
          if (updateInfo) {
            const billBalance = item.bill.balance || 0;
            const remaining =
              Math.round((billBalance - updateInfo.amount) * 100) / 100;
            const adjustmentAmount =
              !isBillForeignCurrency(item.bill) &&
              remaining > 0 &&
              remaining <= 10
                ? remaining
                : 0;
            return {
              ...item,
              amount: updateInfo.amount,
              adjustmentAmount,
            };
          }
          return item;
        });

        return updated;
      });

      setPayInFullUsed(true);
    }, [
      hasForeignCurrency,
      isBillForeignCurrency,
      transactionAmount,
      bankExchangeRate,
      selectedBills,
      isMultipleInstances,
      totalAllocatedAmountsFromOtherInstances,
      showWarning,
    ]);

    // Load more bills
    const loadMoreBills = useCallback(() => {
      if (!billsHasMore || billsLoading || !selectedVendor) return;
      fetchVendorBills(selectedVendor.zoho_vendorid, false);
    }, [billsHasMore, billsLoading, selectedVendor, fetchVendorBills]);

    // Helper functions
    const truncateText = (text: string, maxLength: number): string => {
      if (text.length <= maxLength) {
        return text;
      }
      return text.substring(0, maxLength) + "...";
    };

    const formatIndianNumber = (amount: number): string => {
      const sign = amount < 0 ? "-" : "";
      const absoluteAmount = Math.abs(amount);
      const parts = absoluteAmount.toFixed(2).split(".");
      const integerPart = parts[0];
      const decimalPart = parts[1];
      let formatted = "";
      const reversed = integerPart.split("").reverse();

      for (let i = 0; i < reversed.length; i++) {
        if (i === 3) {
          formatted = "," + formatted;
        } else if (i > 3 && (i - 3) % 2 === 0) {
          formatted = "," + formatted;
        }
        formatted = reversed[i] + formatted;
      }

      return sign + formatted + "." + decimalPart;
    };

    const formatAmount = (amount: number): string => {
      return formatIndianNumber(amount);
    };

    // Calculate forex details
    const calculateForexDetails = () => {
      if (!hasForeignCurrency() || !bankExchangeRate || bankExchangeRate <= 0) {
        return {
          forexGainOrLoss: 0,
          billINRTotal: 0,
          realisationINr: 0,
        };
      }

      const billINRTotal = selectedBills
        .filter((item) => item.isSelected && item.amount > 0)
        .reduce((sum, item) => {
          const billRate = item.bill.exchange_rate || 1;
          return sum + item.amount * billRate;
        }, 0);

      const realisationINr = selectedBills
        .filter((item) => item.isSelected && item.amount > 0)
        .reduce((sum, item) => {
          return sum + item.amount * bankExchangeRate;
        }, 0);

      const forexGainOrLoss = realisationINr - billINRTotal;

      return {
        forexGainOrLoss: Math.round(forexGainOrLoss * 100) / 100,
        billINRTotal: Math.round(billINRTotal * 100) / 100,
        realisationINr: Math.round(realisationINr * 100) / 100,
      };
    };

    const forexDetails = calculateForexDetails();

    // Sync local input states with prop values when they change externally
    useEffect(() => {
      if (isBankExchangeRateFocused) {
        return;
      }
      if (bankExchangeRate !== null) {
        const stringValue = bankExchangeRate.toString();
        const currentParsed = parseFloat(bankExchangeRateInput);
        if (isNaN(currentParsed) || currentParsed !== bankExchangeRate) {
          setBankExchangeRateInput(stringValue);
        }
      } else if (bankExchangeRateInput !== "") {
        setBankExchangeRateInput("");
      }
    }, [bankExchangeRate, isBankExchangeRateFocused]);

    useEffect(() => {
      if (!isAdvancePaymentFocused) {
        if (advancePayment > 0) {
          const stringValue = advancePayment.toString();
          const currentParsed = parseFloat(advancePaymentInput);
          if (isNaN(currentParsed) || currentParsed !== advancePayment) {
            setAdvancePaymentInput(stringValue);
          }
        } else if (advancePaymentInput !== "") {
          setAdvancePaymentInput("");
        }
      }
    }, [advancePayment, isAdvancePaymentFocused]);

    // Sync bill amount/adjustment inputs when they change externally (e.g. Pay in Full)
    useEffect(() => {
      setBillAmountInputs((prev) => {
        let next: Record<string, string> | null = null;
        selectedBills.forEach((item) => {
          if (!item.isSelected || item.amount <= 0) return;
          const billId = item.bill.bill_id;
          if (focusedBillInputsRef.current.has(billId)) return;
          const existingInput = (next ?? prev)[billId];
          const currentParsed = parseFloat(existingInput || "0");
          if (currentParsed !== item.amount || !existingInput) {
            if (!next) next = { ...prev };
            next[billId] = item.amount.toString();
          }
        });
        return next ?? prev;
      });
    }, [selectedBills]);

    useEffect(() => {
      setBillAdjustmentInputs((prev) => {
        let next: Record<string, string> | null = null;
        selectedBills.forEach((item) => {
          const billId = item.bill.bill_id;
          if (!item.isSelected || !shouldShowBillAdjustment(item)) {
            if (prev[billId] !== undefined) {
              if (!next) next = { ...prev };
              delete next[billId];
            }
            return;
          }
          const adjustmentAmount = item.adjustmentAmount || 0;
          const existingInput = (next ?? prev)[billId];
          const currentParsed = parseFloat(existingInput || "0");
          if (currentParsed !== adjustmentAmount || !existingInput) {
            if (!next) next = { ...prev };
            next[billId] =
              adjustmentAmount > 0 ? adjustmentAmount.toString() : "";
          }
        });
        return next ?? prev;
      });
    }, [selectedBills, shouldShowBillAdjustment]);

    return (
      <div ref={ref} className="flex w-full flex-col items-start gap-4 mb-6">
        {/* Vendor Selection */}
        <div className="flex w-full items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-body font-body text-default-font">
              Vendor
            </span>
            <SearchableDropdown
              fetchItems={(searchTerm, limit, offset) =>
                fetchVendors(
                  searchTerm,
                  limit,
                  offset,
                  selectedVendor?.zoho_vendorid,
                  selectedVendor?.vendor_legal_name,
                )
              }
              trigger={
                <Button
                  className="h-8 w-40 flex-none justify-between overflow-hidden"
                  variant="neutral-secondary"
                  iconRight={
                    loadingZohoParty ? (
                      <FeatherLoader className="h-4 w-4 animate-spin" />
                    ) : (
                      <FeatherChevronDown />
                    )
                  }
                  disabled={loadingZohoParty}
                >
                  <span className="truncate text-left">
                    {loadingZohoParty
                      ? "Loading vendor..."
                      : selectedVendor
                        ? truncateText(selectedVendor.vendor_legal_name, 12)
                        : "Select Vendor"}
                  </span>
                </Button>
              }
              getSearchableText={(vendor) => vendor.vendor_legal_name}
              renderItem={(vendor) => vendor.vendor_legal_name}
              getItemKey={(vendor) => vendor.zoho_vendorid}
              onSelect={handleVendorSelect}
              selectedKey={selectedVendor?.zoho_vendorid}
              searchPlaceholder="Search vendors..."
              emptyMessage="No vendors found"
              noResultsMessage="No vendors match your search"
              initialLimit={20}
            />
            {vendorFromSuggestion && autoSelectVendorId && (
              <div className="flex items-center gap-1">
                <Badge variant="brand">Recommended</Badge>
                {recommendationSource && (
                  <Badge variant="neutral">
                    {(() => {
                      switch (recommendationSource) {
                        case "knn":
                          return "K-NN";
                        case "override":
                          return "Override";
                        case "direct_match":
                          return "Direct match";
                        case "amount_match":
                          return "Amount";
                        case "ledger_match":
                          return "Ledger";
                        case "vendormatching":
                          return "LLM";
                        default:
                          return recommendationSource;
                      }
                    })()}
                    {recommendationConfidence != null &&
                      recommendationConfidence !== "" &&
                      ` · ${Math.round(
                        (typeof recommendationConfidence === "string"
                          ? parseFloat(recommendationConfidence)
                          : recommendationConfidence) * 100,
                      )}%`}
                  </Badge>
                )}
              </div>
            )}
          </div>
          {selectedVendor && (
            <div className="flex items-center gap-2">
              <TextField
                className="h-8 w-48"
                label=""
                helpText=""
                icon={<FeatherSearch />}
              >
                <TextField.Input
                  placeholder="Search bills"
                  value={billSearchTerm}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setBillSearchTerm(event.target.value);
                  }}
                />
              </TextField>
            </div>
          )}
          {showRemoveButton && onRemove && (
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={<FeatherX />}
              onClick={onRemove}
            >
              Remove
            </Button>
          )}
        </div>

        {/* Bank Exchange Rate Field - shown only for foreign currency */}
        {hasForeignCurrency() && selectedVendor && (
          <div className="flex w-full flex-col items-start gap-2 mb-4">
            <span className="text-body-bold font-body-bold text-default-font">
              Bank Exchange Rate
            </span>
            <div className="flex w-full items-center gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-default-font">
                  Exchange Rate:
                </span>
                <TextField
                  className="h-auto w-40 flex-none"
                  label=""
                  helpText=""
                >
                  <TextField.Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="Enter rate"
                    value={bankExchangeRateInput}
                    onFocus={() => setIsBankExchangeRateFocused(true)}
                    onBlur={() => setIsBankExchangeRateFocused(false)}
                    onWheel={handleNumberInputWheel}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const inputValue = event.target.value;
                      setBankExchangeRateInput(inputValue);

                      if (inputValue === "") {
                        onBankExchangeRateChange(null);
                        return;
                      }

                      const decimalRegex = /^\d*\.?\d*$/;
                      if (!decimalRegex.test(inputValue)) {
                        return;
                      }

                      if (inputValue === "." || inputValue.endsWith(".")) {
                        const numericValue =
                          inputValue === "." ? null : parseFloat(inputValue);
                        onBankExchangeRateChange(numericValue);
                        return;
                      }

                      const value = parseFloat(inputValue);
                      if (!isNaN(value)) {
                        onBankExchangeRateChange(value);
                      }
                    }}
                  />
                </TextField>
                <span className="text-body font-body text-default-font text-neutral-500">
                  (Required for foreign currency transactions)
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Available Bills */}
        {selectedVendor && (
          <div className="flex w-full flex-col items-start gap-2">
            <div className="flex w-full items-center justify-between">
              <span className="text-body-bold font-body-bold text-default-font">
                Available Bills
                {loadingBills && (
                  <span className="text-body font-body text-neutral-500 ml-2">
                    (Loading...)
                  </span>
                )}
              </span>
              <div className="flex items-center gap-4">
                {!hasForeignCurrency() &&
                  selectedBills.some((item) => item.isSelected) && (
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      onClick={handlePayInFull}
                      disabled={payInFullUsed}
                    >
                      Pay in Full
                    </Button>
                  )}
              </div>
            </div>
            {selectedBills.length > 0 ? (
              selectedBills.map((item) => (
                <div
                  key={item.bill.bill_id}
                  className={`flex w-full items-center rounded-md border border-solid px-4 py-3 ${
                    item.isSelected
                      ? item.amount > 0
                        ? "border-success-600 bg-success-100"
                        : "border-warning-600 bg-warning-50"
                      : "border-neutral-border bg-default-background"
                  }`}
                >
                  {/* Column 1: Select button + Bill number + Vendor name - Fixed width */}
                  <div className="flex items-center gap-4 w-56 flex-shrink-0">
                    <Button
                      className={`h-6 w-6 flex-none ${
                        item.isSelected
                          ? "bg-success-600 text-white"
                          : "border-2 border-dashed border-neutral-300"
                      }`}
                      variant={
                        item.isSelected ? "brand-primary" : "neutral-secondary"
                      }
                      size="small"
                      icon={item.isSelected ? <FeatherCheck /> : undefined}
                      onClick={() => handleBillSelect(item.bill.bill_id)}
                    />
                    <div className="flex flex-col items-start min-w-0">
                      <span className="text-body-bold font-body-bold text-default-font truncate w-full">
                        {item.bill.bill_number || item.bill.bill_id}
                      </span>
                      <span className="text-body font-body text-neutral-500 truncate w-full">
                        {item.bill.vendor_name}
                      </span>
                    </div>
                  </div>

                  {/* Column 2: Full/Paid/Remaining amounts - Fixed width */}
                  <div className="flex flex-1 items-center justify-center gap-8 text-center">
                    <div className="flex flex-col items-center w-20">
                      <span className="text-caption font-caption text-neutral-400">
                        Full
                      </span>
                      <span className="text-body font-body text-default-font">
                        {item.bill.currency_symbol}{" "}
                        {formatAmount(item.bill.total)}
                      </span>
                    </div>
                    <div className="flex flex-col items-center w-20">
                      <span className="text-caption font-caption text-neutral-400">
                        Paid
                      </span>
                      <span className="text-body font-body text-default-font">
                        {item.bill.currency_symbol}{" "}
                        {formatAmount(item.bill.total - item.bill.balance)}
                      </span>
                    </div>
                    <div className="flex flex-col items-center w-24">
                      <span className="text-caption font-caption text-neutral-400">
                        Remaining
                      </span>
                      <span className="text-body font-body text-default-font">
                        {item.bill.currency_symbol}{" "}
                        {formatAmount(item.bill.balance)}
                      </span>
                    </div>
                  </div>

                  {/* Column 3: Amount input - Fixed width */}
                  <div className="flex items-center gap-6 flex-1 justify-end">
                    <div className="flex flex-col items-center gap-1">
                      <TextField
                        className="h-auto w-32 flex-none"
                        label=""
                        helpText=""
                      >
                        <TextField.Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={
                            getMaxAmountForBill(item.bill.bill_id) <
                            item.bill.balance
                              ? `Max: ${
                                  item.bill.currency_symbol
                                }${formatAmount(
                                  getMaxAmountForBill(item.bill.bill_id),
                                )}`
                              : `${item.bill.currency_symbol}${formatAmount(
                                  item.bill.balance,
                                )}`
                          }
                          value={
                            item.isSelected
                              ? billAmountInputs[item.bill.bill_id] !==
                                undefined
                                ? billAmountInputs[item.bill.bill_id]
                                : item.amount > 0
                                  ? item.amount.toString()
                                  : ""
                              : ""
                          }
                          disabled={
                            !item.isSelected ||
                            (isBillForeignCurrency(item.bill) &&
                              (!bankExchangeRate || bankExchangeRate <= 0))
                          }
                          onWheel={handleNumberInputWheel}
                          onFocus={(e) => {
                            focusedBillInputsRef.current.add(item.bill.bill_id);
                          }}
                          onBlur={(e) => {
                            focusedBillInputsRef.current.delete(
                              item.bill.bill_id,
                            );
                          }}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            const inputValue = event.target.value;
                            const billId = item.bill.bill_id;

                            const updateInputValue = (value: string) => {
                              setBillAmountInputs((prev) => ({
                                ...prev,
                                [billId]: value,
                              }));
                            };

                            if (inputValue === "") {
                              updateInputValue("");
                              handleAmountChange(billId, 0);
                              return;
                            }

                            const decimalRegex = /^\d*\.?\d{0,2}$/;
                            if (!decimalRegex.test(inputValue)) {
                              return;
                            }

                            // Real-time validation: Check if the input value would exceed max amount
                            // Only validate if it's a complete number (not ending with decimal point)
                            if (
                              inputValue !== "." &&
                              !inputValue.endsWith(".")
                            ) {
                              const numericValue = parseFloat(inputValue);
                              if (!isNaN(numericValue)) {
                                const maxAllowed = getMaxAmountForBill(billId);
                                const roundedMaxAllowed =
                                  Math.round(maxAllowed * 100) / 100;

                                // If the numeric value exceeds max, prevent the input and show warning
                                if (numericValue > roundedMaxAllowed + 0.001) {
                                  const currencySymbol =
                                    item.bill.currency_symbol ||
                                    item.bill.currency_code ||
                                    "INR";
                                  showWarning(
                                    `Cannot exceed the max amount of ${currencySymbol} ${formatAmount(
                                      roundedMaxAllowed,
                                    )}.`,
                                  );
                                  // Don't update the input - keep the previous valid value
                                  return;
                                }
                              }
                            }

                            if (
                              inputValue === "." ||
                              inputValue.endsWith(".")
                            ) {
                              const numericValue =
                                inputValue === "."
                                  ? 0
                                  : parseFloat(inputValue) || 0;
                              updateInputValue(inputValue);
                              handleAmountChange(billId, numericValue);
                              return;
                            }

                            const value = parseFloat(inputValue) || 0;
                            const roundedValue = Math.round(value * 100) / 100;
                            updateInputValue(inputValue);
                            handleAmountChange(billId, roundedValue);
                          }}
                        />
                      </TextField>
                      {item.isSelected &&
                        isBillForeignCurrency(item.bill) &&
                        bankExchangeRate &&
                        bankExchangeRate > 0 &&
                        item.amount > 0 && (
                          <span className="text-xs text-neutral-500">
                            = ₹{formatAmount(item.amount * bankExchangeRate)}
                          </span>
                        )}
                      {shouldShowBillAdjustment(item) && (
                        <div className="flex flex-col items-center gap-1 mt-1">
                          <TextField
                            className="h-auto w-32 flex-none"
                            label=""
                            helpText=""
                          >
                            <TextField.Input
                              className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Adjustment"
                              value={
                                billAdjustmentInputs[item.bill.bill_id] ?? ""
                              }
                              disabled={!item.isSelected}
                              onWheel={handleNumberInputWheel}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                const inputValue = event.target.value;
                                const billId = item.bill.bill_id;

                                if (inputValue === "") {
                                  setBillAdjustmentInputs((prev) => ({
                                    ...prev,
                                    [billId]: "",
                                  }));
                                  handleAdjustmentChange(billId, 0);
                                  return;
                                }

                                const decimalRegex = /^\d*\.?\d{0,2}$/;
                                if (!decimalRegex.test(inputValue)) {
                                  return;
                                }

                                if (
                                  inputValue !== "." &&
                                  !inputValue.endsWith(".")
                                ) {
                                  const numericValue = parseFloat(inputValue);
                                  if (!isNaN(numericValue)) {
                                    const maxAllowed =
                                      getBillAdjustmentLimit(item);
                                    if (numericValue > maxAllowed + 0.001) {
                                      showWarning(
                                        `Adjustment cannot exceed ${
                                          item.bill.currency_symbol || "₹"
                                        }${formatAmount(maxAllowed)}.`,
                                      );
                                      return;
                                    }
                                  }
                                }

                                setBillAdjustmentInputs((prev) => ({
                                  ...prev,
                                  [billId]: inputValue,
                                }));
                                handleAdjustmentChange(
                                  billId,
                                  parseFloat(inputValue) || 0,
                                );
                              }}
                            />
                          </TextField>
                          <span className="text-caption font-caption text-neutral-500 text-center leading-tight">
                            Rounding adjustment
                          </span>
                        </div>
                      )}
                    </div>
                    <Button
                      className={
                        item.isSelected ? "bg-success-600 text-white" : ""
                      }
                      size="small"
                      onClick={() => handleBillSelect(item.bill.bill_id)}
                    >
                      {item.isSelected ? "Selected" : "Select"}
                    </Button>
                  </div>
                </div>
              ))
            ) : !loadingBills ? (
              <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-6">
                <span className="text-body font-body text-neutral-500">
                  No bills found for {selectedVendor.vendor_legal_name}
                </span>
              </div>
            ) : null}
            {billsHasMore && (
              <div className="flex w-full items-center justify-center pt-2">
                <Button
                  variant="neutral-secondary"
                  size="small"
                  onClick={loadMoreBills}
                  disabled={billsLoading}
                  loading={billsLoading}
                >
                  {billsLoading ? "Loading..." : "Load More Bills"}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Forex Calculations */}
        {hasForeignCurrency() &&
          bankExchangeRate &&
          bankExchangeRate > 0 &&
          selectedBills.some((item) => item.isSelected && item.amount > 0) &&
          selectedVendor && (
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Forex Calculations
              </span>
              <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Forex Gain/Loss
                  </span>
                  <span
                    className={`text-body-bold font-body-bold ${
                      forexDetails.forexGainOrLoss >= 0
                        ? "text-success-600"
                        : "text-error-600"
                    }`}
                  >
                    ₹{formatAmount(forexDetails.forexGainOrLoss)}
                  </span>
                </div>
              </div>
            </div>
          )}

        {/* Advance Payment */}
        {(knockOffOpeningBalance ||
          isMultipleInstances ||
          (!hasForeignCurrency() && selectedVendor)) && (
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              {knockOffOpeningBalance ? "Opening balance" : "Advance Payment"}
            </span>
            <div className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
              <div className="flex items-center gap-4">
                <span className="text-body font-body text-default-font">
                  {knockOffOpeningBalance
                    ? "Opening balance"
                    : "Advance Payment Amount"}
                </span>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-4">
                  <TextField
                    className="h-auto w-40 flex-none"
                    label=""
                    helpText=""
                  >
                    <TextField.Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={advancePaymentInput}
                      disabled={
                        !isMultipleInstances ||
                        (!knockOffOpeningBalance && !canAdvance)
                      }
                      onFocus={() => setIsAdvancePaymentFocused(true)}
                      onBlur={() => setIsAdvancePaymentFocused(false)}
                      onWheel={handleNumberInputWheel}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        if (!isMultipleInstances) return;
                        const inputValue = event.target.value;
                        setAdvancePaymentInput(inputValue);

                        if (inputValue === "") {
                          setAdvancePayment(0);
                          return;
                        }

                        const decimalRegex = /^\d*\.?\d{0,2}$/;
                        if (!decimalRegex.test(inputValue)) {
                          return;
                        }

                        const value = parseFloat(inputValue) || 0;
                        const roundedValue = Math.round(value * 100) / 100;
                        setAdvancePayment(roundedValue);
                      }}
                    />
                  </TextField>
                  <span className="text-body font-body text-default-font w-8">
                    ₹
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

VendorPaymentReco.displayName = "VendorPaymentReco";

export default VendorPaymentReco;
